import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Calendar, MapPin, Users, Loader2, CheckCircle2, Ticket } from "lucide-react";
import api, { API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString("en-NG", { weekday: "short", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function Events() {
  const [events, setEvents] = useState([]);
  const [lgas, setLgas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const load = () => { setLoading(true); api.get("/events").then((r) => setEvents(r.data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); api.get("/structure/lgas").then((r) => setLgas(r.data.lgas)).catch(() => {}); }, []);

  const upcoming = events.filter((e) => new Date(e.event_date) >= new Date(Date.now() - 864e5));

  return (
    <div>
      <section className="topo-bg text-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <span className="eyebrow text-secondary">Events & Programs</span>
          <h1 className="font-heading font-black text-4xl md:text-5xl mt-3">Upcoming Events</h1>
          <p className="mt-4 text-lg text-emerald-50 max-w-2xl">Rallies, town halls, empowerment drives and ward meetings — register to be part of the movement's programs.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-16">
        {loading ? <div className="py-20 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" /></div>
          : events.length === 0 ? (
            <div className="text-center py-20 text-muted-foreground"><Calendar className="w-10 h-10 mx-auto mb-3 opacity-40" /><p className="font-semibold">No events scheduled yet.</p><p className="text-sm">Check back soon for upcoming programs.</p></div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {events.map((e, i) => {
                const full = e.capacity && e.registered >= e.capacity;
                const past = new Date(e.event_date) < new Date(Date.now() - 864e5);
                return (
                  <motion.div key={e.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                    data-testid={`event-card-${e.id}`} className="bg-white rounded-3xl overflow-hidden border border-slate-100 shadow-sm group flex flex-col">
                    {e.image_path
                      ? <div className="aspect-video overflow-hidden"><img src={`${API}/files/${e.image_path}`} alt={e.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                      : <div className="aspect-video topo-bg flex items-center justify-center"><Calendar className="w-12 h-12 text-secondary/70" /></div>}
                    <div className="p-5 flex flex-col flex-1">
                      {e.lga && <span className="text-[11px] font-bold uppercase bg-primary/10 text-primary px-2 py-0.5 rounded-full w-fit">{e.lga}</span>}
                      <h3 className="font-heading font-bold text-lg mt-2">{e.title}</h3>
                      <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed line-clamp-3 flex-1">{e.description}</p>
                      <div className="mt-3 space-y-1.5 text-sm">
                        <div className="flex items-center gap-2 text-slate-700"><Calendar className="w-4 h-4 text-primary shrink-0" /> {fmtDate(e.event_date)}</div>
                        <div className="flex items-center gap-2 text-slate-700"><MapPin className="w-4 h-4 text-primary shrink-0" /> {e.location}</div>
                        <div className="flex items-center gap-2 text-slate-700"><Users className="w-4 h-4 text-primary shrink-0" /> {e.registered} registered{e.capacity ? ` · ${e.capacity} max` : ""}</div>
                      </div>
                      <Button data-testid={`event-register-${e.id}`} disabled={full || past} onClick={() => setActive(e)} className="mt-4 rounded-full bg-primary font-bold gap-2">
                        <Ticket className="w-4 h-4" /> {past ? "Event Ended" : full ? "Fully Booked" : "Register"}
                      </Button>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
      </section>

      <RegisterDialog event={active} lgas={lgas} onClose={() => setActive(null)} onDone={load} />
    </div>
  );
}

function RegisterDialog({ event, lgas, onClose, onDone }) {
  const [f, setF] = useState({ name: "", phone: "", email: "", lga: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const valid = f.name && f.phone.length >= 11;

  useEffect(() => { if (event) { setF({ name: "", phone: "", email: "", lga: "" }); setDone(false); } }, [event]);

  const submit = async () => {
    setLoading(true);
    try {
      await api.post(`/events/${event.id}/register`, f);
      setDone(true); onDone();
      toast.success("You're registered!");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={!!event} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>{done ? "Registration Confirmed" : `Register — ${event?.title}`}</DialogTitle></DialogHeader>
        {done ? (
          <div className="text-center py-4">
            <CheckCircle2 className="w-14 h-14 text-primary mx-auto mb-3" />
            <p className="text-muted-foreground">See you at <b>{event?.title}</b>! We'll send a reminder to {f.phone}.</p>
            <Button className="mt-5 rounded-full bg-primary font-bold" onClick={onClose} data-testid="event-reg-close">Done</Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div><Label className="mb-1.5 block text-sm font-semibold">Full Name</Label><Input data-testid="event-reg-name" value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" /></div>
            <div><Label className="mb-1.5 block text-sm font-semibold">Phone Number</Label><Input data-testid="event-reg-phone" value={f.phone} onChange={(e) => set("phone", e.target.value.replace(/\D/g, ""))} placeholder="08030000000" maxLength={11} /></div>
            <div><Label className="mb-1.5 block text-sm font-semibold">Email (optional)</Label><Input data-testid="event-reg-email" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" /></div>
            <div><Label className="mb-1.5 block text-sm font-semibold">LGA (optional)</Label>
              <Select value={f.lga} onValueChange={(v) => set("lga", v)}>
                <SelectTrigger data-testid="event-reg-lga"><SelectValue placeholder="Select LGA" /></SelectTrigger>
                <SelectContent>{lgas.map((l) => <SelectItem key={l.code} value={l.name}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button data-testid="event-reg-submit" onClick={submit} disabled={!valid || loading} className="w-full rounded-full bg-primary font-bold gap-2 py-6">{loading && <Loader2 className="w-4 h-4 animate-spin" />} Confirm Registration</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
